import CampaignFooterStatus from '@components/campaign-footer-status/campaign-footer-status.component'
import CanReceive from '@components/can-receive/can-receive.component'
import ExtraRequirements from '@components/extra-requirements/extra-requirements.component'
import PerfilRequirements from '@components/perfil-requirements/perfil-requirements.component'
import ResearchPreRecruitment from '@components/research-pre-recruitment/research-pre-recruitment.component'
import SelectSocialNetwork from '@components/select-social-network/select-social-network.component'
import TtcmWarning from '@components/ttcm-warning/ttcm-warning.component'
import {
  Button,
  Campaign,
  Environment,
  FitRequirements,
  HubCampaignsService,
  LogService,
  ObjectHelper,
  SocialNetworksAvailables,
  SocialNetworksInfoParams,
  ToastHelper,
  User,
  UserInDetails,
  localForageHelper,
  onEnvironmentChange,
  onSocialNetworkConnection,
} from '@hub/shared'
import BrandRequirements from '@src/components/brand-requirements/brand-requirements.component'
import ContentByProfile from '@src/components/content-by-profile/content-by-profile.component'
import ErrorMessage from '@src/enums/error-message.enum'
import icons from '@src/enums/icons.enum'
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { SubscriptionLike } from 'rxjs'
import ModalAcceptCampaign from '../modal-accept-campaign/modal-accept-campaign.component'
import ModalContractAccepted from '../modal-contract-accepted/modal-contract-accepted.component'
import ModalRemoveInterest from '../modal-remove-interest/modal-remove-interest.component'

import { SqLoader } from '@squidit/react-css'
import './campaign-footer.component.scoped.scss'

const DEFAULT_STYLE: CSSProperties = {
  backgroundColor: 'var(--background)',
  borderTop: '1px solid var(--background_secondary)',
  padding: '2rem 1rem',
}

export interface CampaignObject {
  campaign: Campaign
  fitRequirements?: FitRequirements
  users: UserInDetails[]
  hasVacancies?: boolean
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  user: User | null
  campaignObject?: CampaignObject | null
  registerRequirements?: boolean
  onChange?: (value: any) => any
  updateCampaignObject?: (update: boolean) => any
}

interface LayoutButtons {
  label: any
  colorPattern: {
    color: string
    textColor: string
    borderColor: string
  }
  function: any
  icon?: any
}

interface LayoutMessages {
  label: string
  icon: any
  iconColor: string
}

enum FooterCase {
  linkSocialNetwork = 'linkSocialNetwork',
  recruited = 'recruited',
  customerReproved = 'customerReproved',
  influencerRefused = 'influencerRefused',
  waitingApproval = 'waitingApproval',
  vacanciesEnd = 'vacanciesEnd',
  renewToken = 'renewToken',
  dontHaveProfile = 'dontHaveProfile',
  registerClose = 'registerClose',
  registerFalse = 'registerFalse',
  profileFalse = 'profileFalse',
  campaignFinished = 'campaignFinished',
  profileIsEvaluated = 'profileIsEvaluated',
  contractSent = 'contractSent',
  waitingForContract = 'waitingForContract',
  default = 'default',
}

export default ({ user, campaignObject, registerRequirements, onChange, updateCampaignObject }: Props) => {
  const logService = useMemo(() => new LogService(), [])
  const hubCampaignsService = useMemo(() => new HubCampaignsService(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const environmentChange = useRef<SubscriptionLike | undefined>(undefined)
  const objectHelper = useMemo(() => new ObjectHelper(), [])
  const [t] = useTranslation('campaignFooter')
  const [impersonateObject, setImpersonateObject] = useState<any>(null)
  const [openContract, setOpenContract] = useState(false)
  const [openModalRemoveInterest, setOpenModalRemoveInterest] = useState(false)
  const [openModalContractAccepted, setOpenModalContractAccepted] = useState(false)
  const [userState, setUserState] = useState<User | null>(user)
  const [loading, setLoading] = useState(true)
  const [loadingSelect, setLoadingSelect] = useState(false)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [userSelected, setUserSelected] = useState<UserInDetails | null>(null)
  const [buttonSelected, setButtonSelected] = useState({} as LayoutButtons)
  const [messageSelected, setMessageSelected] = useState<LayoutMessages | null>({} as LayoutMessages)
  const [users, setUsers] = useState([] as UserInDetails[])
  const [footerCaseSelected, setFooterCaseSelected] = useState<FooterCase | null>(null)
  const [campaignIdState, setCampaignIdState] = useState<string | undefined>('')
  const [authUrl, setAuthUrl] = useState('')
  const [state, setState] = useState({
    register: true,
    perfil: true,
    extra: true,
    research: true,
  })

  const navigate = useNavigate()
  const idNetwork = useMemo(() => {
    const queryParams = new URLSearchParams(location?.search)
    return queryParams.get('idNetwork')
  }, [])

  const params: SocialNetworksInfoParams = useParams<any>()
  params.idProfile = idNetwork || ''

  const buttonColorsPattern = useCallback((color?: 'transparent' | 'yellow') => {
    if (color === 'transparent') {
      return {
        color: 'transparent',
        textColor: 'var(--primary_color)',
        borderColor: 'var(--primary_color)',
      }
    }
    if (color === 'yellow') {
      return {
        color: 'var(--yellow_light)',
        textColor: 'var(--yellow_dark)',
        borderColor: 'var(--gold)',
      }
    }
    return {
      color: 'var(--primary_color)',
      textColor: 'var(--secondary_color)',
      borderColor: 'var(--primary_color)',
    }
  }, [])

  const redirect = useCallback(
    (link: string) => {
      navigate(link)
    },
    [navigate]
  )

  const onRenewToken = useCallback(
    async (idProfile: string, socialNetwork: string) => {
      await localForageHelper.setItem('actions', { action: 'renew', idProfile })
      window.location.href = `${authUrl}/${socialNetwork === 'instagram' ? 'facebook' : socialNetwork}?squid-callback=${
        window.location.href?.split('?')[0]
      }`
    },
    [authUrl]
  )

  const showInterest = useCallback(() => {
    setLoadingSubmit(true)
    hubCampaignsService
      .postCampaignsAction(
        campaignObject?.campaign?._id,
        userSelected?.idNetwork,
        'accepted',
        '',
        campaignObject?.campaign?.activation?.suggestion && userSelected?.status !== 'inContact'
      )
      .then((_) => setOpenModalContractAccepted(true))
      .catch((err) => {
        logService.error({ message: err?.data?.errorCode ?? t('errors.defaultError') })
        const messageError = err?.data?.errorCode ? t(`errors.${(ErrorMessage as any)[err?.data?.errorCode]}`) : t('errors.defaultError')
        toast.error(messageError, {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 10000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setLoadingSubmit(false)
      })
  }, [
    hubCampaignsService,
    campaignObject?.campaign?._id,
    campaignObject?.campaign?.activation?.suggestion,
    userSelected?.idNetwork,
    userSelected?.status,
    logService,
    toast,
    t,
  ])

  const confirmButtons = useMemo(
    () => ({
      linkSocialNetwork: {
        label: t('buttonLabels.linkSocialNetwork'),
        colorPattern: buttonColorsPattern(),
        function: () =>
          onSocialNetworkConnection.next({
            socialNetwork: campaignObject?.campaign?.socialNetworks?.[0] as SocialNetworksAvailables,
            connect: true,
          }),
        icon: 'fa-solid fa-link',
      },
      seeCampaign: {
        label: t('buttonLabels.seeCampaign'),
        colorPattern: buttonColorsPattern('transparent'),
        function: () => redirect(`/campaigns/${campaignIdState}/details/${userSelected?.idNetwork}`),
      },
      seeMoreCampaigns: {
        label: t('buttonLabels.seeMoreCampaigns'),
        colorPattern: buttonColorsPattern('transparent'),
        function: () => redirect('/campaigns'),
      },
      waitingApproval: {
        label: t('buttonLabels.waitingApproval'),
        colorPattern: buttonColorsPattern('transparent'),
        function: () => setOpenModalRemoveInterest(true),
      },
      renewToken: {
        label: t('buttonLabels.renewToken'),
        colorPattern: null,
        function: () => onRenewToken(userSelected?.idNetwork, userSelected?.socialNetwork),
        icon: 'fa-solid fa-triangle-exclamation',
      },
      showInterest: {
        label: t('buttonLabels.showInterest'),
        colorPattern: buttonColorsPattern(),
        function: () => showInterest(),
      },
      wantParticipate: {
        label: t('buttonLabels.wantParticipate'),
        colorPattern: buttonColorsPattern(),
        function: () => setOpenContract(true),
      },
      signContract: {
        label: t('buttonLabels.signContract'),
        colorPattern: buttonColorsPattern(),
        function: () => setOpenContract(true),
      },
    }),
    [t, buttonColorsPattern, userSelected, campaignIdState, onRenewToken, redirect, showInterest, campaignObject?.campaign?.socialNetworks]
  )

  const messagePattern = useCallback(
    (value: FooterCase, icon: string, iconColor?: string) => {
      return {
        label: t(`statusMessages.${value}`),
        icon: icon,
        iconColor: iconColor ? iconColor : icon === 'fa-solid fa-circle-check' ? 'var(--green)' : 'var(--red)',
      }
    },
    [t]
  )

  const getDefaultButtonMessage = useCallback(() => {
    if (userSelected?.status === 'inContact') {
      return confirmButtons['signContract']
    }
    return confirmButtons['showInterest']
  }, [userSelected?.status, confirmButtons])

  const objectCases = useMemo(
    () => ({
      linkSocialNetwork: () => {
        setButtonSelected(confirmButtons[FooterCase.linkSocialNetwork])
        setMessageSelected(null)
        return FooterCase.linkSocialNetwork
      },
      recruited: () => {
        setButtonSelected(confirmButtons['seeCampaign'])
        setMessageSelected(messagePattern(FooterCase.recruited, 'fa-solid fa-circle-check'))
        return FooterCase.recruited
      },
      customerReproved: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.customerReproved, 'fa-solid fa-circle-xmark'))
        return FooterCase.customerReproved
      },
      influencerRefused: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.influencerRefused, 'fa-solid fa-circle-xmark'))
        return FooterCase.influencerRefused
      },
      waitingApproval: () => {
        setButtonSelected(confirmButtons[FooterCase.waitingApproval])
        setMessageSelected(messagePattern(FooterCase.waitingApproval, 'fa-solid fa-circle-check', 'var(--gold)'))
        return FooterCase.waitingApproval
      },
      profileIsEvaluated: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.waitingApproval, 'fa-solid fa-circle-check', 'var(--gold)'))
        return FooterCase.waitingApproval
      },
      vacanciesEnd: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.vacanciesEnd, 'fa-solid fa-circle-xmark'))
        return FooterCase.vacanciesEnd
      },
      renewToken: () => {
        setButtonSelected(confirmButtons['renewToken'])
        setMessageSelected(null)
        return FooterCase.renewToken
      },
      dontHaveProfile: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.dontHaveProfile, 'fa-solid fa-circle-xmark'))
        return FooterCase.dontHaveProfile
      },
      registerClose: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.registerClose, 'fa-solid fa-circle-xmark'))
        return FooterCase.registerClose
      },
      registerFalse: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.registerFalse, 'fa-solid fa-circle-xmark'))
        return FooterCase.registerFalse
      },
      profileFalse: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.profileFalse, 'fa-solid fa-circle-xmark'))
        return FooterCase.profileFalse
      },
      campaignFinished: () => {
        setButtonSelected(confirmButtons['seeMoreCampaigns'])
        setMessageSelected(messagePattern(FooterCase.campaignFinished, 'fa-solid fa-circle-xmark'))
        return FooterCase.campaignFinished
      },
      default: () => {
        setButtonSelected(getDefaultButtonMessage())
        if (userSelected?.status === 'inContact') {
          if (['sent', 'resent'].includes(userSelected?.contractStatus)) {
            setMessageSelected(messagePattern(FooterCase.contractSent, icons?.fileContract, 'var(--green)'))
          } else {
            setMessageSelected(messagePattern(FooterCase.waitingForContract, icons?.fileContract, 'var(--red)'))
          }
        } else {
          setMessageSelected(null)
        }
        return FooterCase.default
      },
    }),
    [confirmButtons, messagePattern, getDefaultButtonMessage, userSelected?.contractStatus, userSelected?.status]
  )

  const handleCheckRenewToken = useCallback(() => {
    const statusProvider =
      campaignObject?.campaign?.socialNetworks?.[0] === 'youtube' ? 'google-oauth2' : campaignObject?.campaign?.socialNetworks?.[0]

    const isTokenExpired = user?.statusProviders?.[statusProvider]?.find((e) => e.id === userSelected?.idNetwork)?.tokenExpired
    const isProfileMacro = !!user?.[campaignObject?.campaign?.socialNetworks?.[0]]?.find((e) => e.id === userSelected?.idNetwork)?.macro

    return isTokenExpired && !isProfileMacro
  }, [campaignObject, userSelected, user])

  const onParseRecruitment = useCallback(() => {
    const hasRenewToken: boolean = handleCheckRenewToken()
    let footerSelected

    const checkTokenAndApplyStatusWaiting = () => {
      if (!hasRenewToken) {
        footerSelected = FooterCase.waitingApproval
        if (userSelected.inputMode !== 'opportunity') {
          footerSelected = FooterCase.profileIsEvaluated
        }
      } else {
        footerSelected = FooterCase.renewToken
      }
    }

    const checkTokenAndVacanciesApplyStatusDefault = () => {
      if (!hasRenewToken) {
        if (!campaignObject?.hasVacancies) {
          footerSelected = FooterCase.vacanciesEnd
        } else {
          footerSelected = FooterCase.default
        }
      } else {
        footerSelected = FooterCase.renewToken
      }
    }

    switch (userSelected?.status) {
      case 'recruited':
        footerSelected = FooterCase.recruited
        break
      case 'customerReproved':
      case 'removed':
        footerSelected = FooterCase.customerReproved
        break
      case 'influencerRefused':
        footerSelected = FooterCase.influencerRefused
        break
      case 'approved':
      case 'preApproved':
      case 'customerApproved':
        checkTokenAndApplyStatusWaiting()
        break
      case 'inContact':
        checkTokenAndVacanciesApplyStatusDefault()
        break
      default:
        footerSelected = FooterCase.default
    }
    return objectCases[footerSelected]()
  }, [campaignObject, userSelected, objectCases, handleCheckRenewToken])

  const parseCase = useCallback(() => {
    if (
      campaignObject?.campaign?.status &&
      ['archived', 'finished'].includes(campaignObject?.campaign?.status) &&
      (!userSelected?.status || userSelected?.status === 'influencerRefused')
    ) {
      return objectCases[FooterCase.campaignFinished]()
    }

    if (!users?.length) {
      return objectCases[FooterCase.linkSocialNetwork]()
    }

    if (userSelected?.status) {
      return onParseRecruitment()
    } else {
      const hasRenewToken = handleCheckRenewToken()
      if (hasRenewToken) {
        return objectCases[FooterCase.renewToken]()
      }
      if (campaignObject?.campaign?.activation?.suggestion) {
        if (!campaignObject?.campaign?.activation?.suggestionVisible) {
          return objectCases[FooterCase.registerClose]()
        }
        if (!campaignObject?.hasVacancies) {
          return objectCases[FooterCase.vacanciesEnd]()
        }
      } else {
        return objectCases[FooterCase.dontHaveProfile]()
      }
    }
    if (!state?.register) {
      return objectCases[FooterCase.registerFalse]()
    }
    if (!state?.perfil) {
      return objectCases[FooterCase.profileFalse]()
    }
    return objectCases[FooterCase.default]()
  }, [users, objectCases, userSelected, campaignObject, state, onParseRecruitment, handleCheckRenewToken])

  const isDisabled = useMemo(() => {
    return (
      footerCaseSelected === FooterCase.default &&
      ((campaignObject?.campaign?.activation?.suggestion &&
        userSelected?.status !== 'inContact' &&
        !Object.values(state).every((fieldValue) => fieldValue)) ||
        (!!impersonateObject && !impersonateObject?.impersonateAdmin) ||
        (userSelected?.status === 'inContact' && !['sent', 'resent'].includes(userSelected?.contractStatus)))
    )
  }, [
    campaignObject?.campaign?.activation?.suggestion,
    footerCaseSelected,
    impersonateObject,
    state,
    userSelected?.contractStatus,
    userSelected?.status,
  ])

  useEffect(() => {
    if (user && (campaignIdState !== campaignObject?.campaign?._id || !objectHelper.compareObjects(userState, user))) {
      setLoading(true)
      setCampaignIdState(campaignObject?.campaign?._id)
      const usersByLocalStorage = user[campaignObject?.campaign?.socialNetworks?.[0]]
      for (const aux in usersByLocalStorage) {
        if (
          campaignObject?.users?.find((e) => e.idNetwork === usersByLocalStorage?.[aux]?.id) &&
          !users?.find((e) => e.idNetwork === usersByLocalStorage?.[aux]?.id)
        ) {
          users?.push(campaignObject?.users?.find((e) => e.idNetwork === usersByLocalStorage?.[aux]?.id))
        } else if (!users?.find((e) => e.idNetwork === usersByLocalStorage?.[aux]?.id)) {
          users?.push({
            squidId: user?.squidId,
            idNetwork: usersByLocalStorage?.[aux]?.id,
            socialNetwork: campaignObject?.campaign?.socialNetworks?.[0] || '',
            username: usersByLocalStorage?.[aux]?.username,
            status: '',
            hasSawWarn: false,
            hasSawSurvey: false,
            customPayment: false,
          })
        }
        setUsers(users)
      }
      setLoading(false)
    }

    if (!!state.register !== !!registerRequirements) {
      setState((s) => {
        return {
          ...s,
          register: !!registerRequirements,
        }
      })
    }
  }, [campaignIdState, campaignObject, objectHelper, userState, user, users, registerRequirements, parseCase, state.register])

  useEffect(() => {
    const idNetwork = new URLSearchParams(window.location.search).get('idNetwork')
    if (!userSelected) {
      const userFind = users?.find((e) => e?.idNetwork === idNetwork)
      if (userFind) {
        setUserSelected(userFind)
      } else if (users?.length > 0) {
        setUserSelected({ ...users?.[0] })
      } else {
        setUserSelected(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users])

  useEffect(() => {
    if (userSelected?.idNetwork || !userSelected) {
      const caseReturn = parseCase()
      if (![FooterCase.profileFalse, FooterCase.default].includes(caseReturn)) {
        setLoadingSelect(false)
      }
      setFooterCaseSelected(caseReturn)
    }
  }, [userSelected, parseCase])

  useEffect(() => {
    if (onChange && typeof onChange === 'function') {
      onChange(userSelected)
    }
  }, [userSelected, onChange])

  useEffect(() => {
    environmentChange.current = onEnvironmentChange.subscribe((environment: Environment) => {
      setAuthUrl(environment?.squidApis?.proxy)
    })

    return () => environmentChange?.current?.unsubscribe()
  }, [])

  useEffect(() => {
    localForageHelper.getItem('impersonateUser').then((impersonateUser) => setImpersonateObject(impersonateUser))
  }, [])

  const handleChange = useCallback(
    (field: string, value: boolean) => {
      if ((state as any)[field] !== value) {
        setState({ ...state, [field]: value })
      }
    },
    [state]
  )

  return (
    <section className="campaign-footer">
      {loading ? (
        <div className="loading-campaign-footer">
          <SqLoader size="small" />
        </div>
      ) : (
        <div>
          <header>
            <SelectSocialNetwork
              socialNetwork={campaignObject?.campaign?.socialNetworks?.[0]}
              users={users}
              userSelected={userSelected}
              footerCase={footerCaseSelected}
              loading={loadingSelect}
              onChange={(value) => {
                setFooterCaseSelected(null)
                for (const field in state) {
                  state[field] = true
                  setState({ ...state })
                }
                setUserSelected(value)
              }}
            />
          </header>
          <main>
            <ContentByProfile
              scope={campaignObject?.users?.find((user) => user?.idNetwork === userSelected?.idNetwork)?.scope || null}
              style={DEFAULT_STYLE}
            />
            <CanReceive campaign={campaignObject?.campaign} user={userSelected} style={DEFAULT_STYLE} />
            <BrandRequirements
              scope={campaignObject?.users?.find((user) => user?.idNetwork === userSelected?.idNetwork)?.scope || null}
              style={DEFAULT_STYLE}
            />
            {campaignObject?.campaign?.isTcm ? <TtcmWarning /> : null}
            {!userSelected?.status && campaignObject?.campaign?.activation?.suggestion ? (
              <div>
                {[FooterCase.profileFalse, FooterCase.default].includes(footerCaseSelected) ? (
                  <PerfilRequirements
                    suggestionRules={campaignObject?.campaign?.activation?.suggestionRules}
                    fitRequirements={campaignObject?.fitRequirements}
                    userSelected={userSelected}
                    user={user}
                    onChange={(value) => handleChange('perfil', !!value)}
                    onLoading={(value) => setLoadingSelect(value)}
                    style={DEFAULT_STYLE}
                  />
                ) : null}
                {footerCaseSelected === FooterCase.default ? (
                  <div>
                    <ExtraRequirements
                      suggestionRequirements={campaignObject?.campaign?.activation?.suggestionRequirements}
                      onChange={(value) => handleChange('extra', !!value)}
                      style={DEFAULT_STYLE}
                    />
                    <ResearchPreRecruitment
                      typeFormLink={campaignObject?.campaign?.activation?.typeFormLink}
                      solution={campaignObject?.campaign?.planning?.solution}
                      campaignId={campaignIdState}
                      squidId={userSelected?.squidId}
                      idNetwork={userSelected?.idNetwork}
                      onChange={(value) => handleChange('research', !!value)}
                      style={DEFAULT_STYLE}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {messageSelected?.label ? (
              <CampaignFooterStatus
                icon={<i className={`${messageSelected?.icon}`} style={{ color: messageSelected?.iconColor }}></i>}
                label={messageSelected?.label}
                style={DEFAULT_STYLE}
              />
            ) : null}
          </main>
          <footer>
            <Button
              id="campaign-footer-cancel"
              onClick={() =>
                !campaignObject?.campaign?.activation?.suggestion && footerCaseSelected === FooterCase.default
                  ? setOpenModalRemoveInterest(true)
                  : redirect('/campaigns')
              }
              size="sm"
              color="transparent"
              textColor="var(--gray)"
              borderColor="var(--gray)"
            >
              <span>
                {!campaignObject?.campaign?.activation?.suggestion && footerCaseSelected === FooterCase.default
                  ? t('buttonLabels.refuse')
                  : t('buttonLabels.cancel')}
              </span>
            </Button>
            <Button
              id={userSelected?.status === 'inContact' && userSelected?.scope ? 'sign_contract' : 'campaign-footer-accept'}
              onClick={buttonSelected?.function}
              size="sm"
              color={buttonSelected?.colorPattern?.color}
              textColor={buttonSelected?.colorPattern?.textColor}
              borderColor={buttonSelected?.colorPattern?.borderColor}
              loading={loadingSubmit}
              disabled={isDisabled}
            >
              <span>
                {buttonSelected?.icon ? (
                  <i className={`${buttonSelected?.icon} mr-2`} style={{ color: buttonSelected?.colorPattern?.textColor }}></i>
                ) : null}
                {buttonSelected?.label}
              </span>
            </Button>
          </footer>
        </div>
      )}
      <ModalAcceptCampaign
        openModal={openContract}
        campaignId={campaignIdState}
        idNetwork={userSelected?.idNetwork}
        opportunity={campaignObject?.campaign?.activation?.suggestion && userSelected?.status !== 'inContact'}
        justCloseModal={
          footerCaseSelected === FooterCase.influencerRefused || (footerCaseSelected === FooterCase.default && !userSelected?.status)
        }
        onOpenModalRemoveInterest={() => setOpenModalRemoveInterest(true)}
        onOpenModalContractAccepted={() => setOpenModalContractAccepted(true)}
        onCloseModal={() => setOpenContract(false)}
      />
      <ModalRemoveInterest
        refuse={footerCaseSelected === FooterCase.default}
        openModal={openModalRemoveInterest}
        opportunity={campaignObject?.campaign?.activation?.suggestion && userSelected?.status !== 'inContact'}
        campaignId={campaignIdState}
        campaignName={campaignObject?.campaign?.exhibitionName}
        idNetwork={userSelected?.idNetwork}
        onCloseModal={() => setOpenModalRemoveInterest(false)}
        onCloseModalContract={() => setOpenContract(false)}
      />
      <ModalContractAccepted
        openModal={openModalContractAccepted}
        campaignId={campaignIdState}
        idNetwork={userSelected?.idNetwork}
        opportunity={campaignObject?.campaign?.activation?.suggestion && userSelected?.status !== 'inContact'}
        userScope={userSelected?.scope}
        campaignObject={campaignObject}
        onCloseModal={() => {
          setOpenModalContractAccepted(false)
          updateCampaignObject(true)
        }}
      />
    </section>
  )
}
